import { green } from '@ant-design/colors';
import {
  CheckCircleOutlined,
  CopyOutlined,
  EyeOutlined,
  FormOutlined,
  MessageOutlined,
  MinusCircleOutlined,
  UserAddOutlined
} from '@ant-design/icons';
import {
  Button,
  Form,
  Modal,
  Popover,
  Space,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import CustomAutocomplete from '../../components/CustomAutocomplete';
import ResponsiveContainer from '../../components/ResponsiveContainer';
import TableAndFilters from '../../components/TableAndFilters';
import { CONFIG } from '../../constants';
import UserContext from '../../context/user-context';
import { ESTADOS_COTIZACION, errorHandler, toUpperCase } from '../../utils';
import { instanceRef } from '../../utils/axios';
import { fieldTypes } from '../../utils/filter-utils';

const styles = {
  form: { textAlign: 'left' },
  card: { marginBottom: 10, textAlign: 'left' }
};

function CotizacionMasivaList() {
  const { user } = useContext(UserContext);
  const [formUser] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [token, setToken] = useState();
  const [tokenEnviar, setTokenEnviar] = useState();
  const [columns, setColumns] = useState([]);
  const tableRef = useRef();
  const [loading, setLoading] = useState();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const enviarSmsMail = async (record) => {
    try {
      setLoading(true);
      setTokenEnviar(record.token);
      const reCaptchaToken = await executeRecaptcha(
        'cotizacionmasiva/enviarSmsMail'
      );
      const dataEnviar = {
        reCaptchaToken,
        producto: record.id_producto,
        usuarioVendedor: record.id_vendedor,
        token: record.token
      };
      const mensajeEnviado = record.correo
        ? `¡La cotización ha sido enviada! Recibirá un e-mail y SMS.`
        : `¡La cotización ha sido enviada! Recibirá un SMS.`;
      await instanceRef.instance.post(
        `cotizacionmasiva/enviarSmsMail`,
        dataEnviar
      );
      message.info(mensajeEnviado);
    } catch (error) {
      message.error('La cotización no pudo ser enviada');
    }
    setLoading(false);
  };

  const handleOpenUrl = (url) => {
    window.open(url);
  };
  const handleGetNewToken = async (tokenOriginal, idProducto) => {
    setLoading(true);
    try {
      let url;
      if (idProducto === 5) {
        url = `${window.location.origin}/comercio/${tokenOriginal}`;
      } else if (idProducto === 1) {
        url = `${window.location.origin}/vehiculo/${tokenOriginal}`;
      } else {
        const response = await instanceRef.instance.post(
          `/tokenExpirables/regenerar-from-token-original?flujo=cotizacionmasiva&tokenOriginal=${tokenOriginal}`
        );
        if (response.data) {
          url = `${window.location.origin}/solicitar-mail/${response.data.message}/masivas`;
        }
      }
      if (url) {
        navigator.clipboard.writeText(url);
        message.success('Se copió el enlace al portapapeles');
      }
      setLoading(false);
    } catch (err) {
      errorHandler('Error al obtener url', err);
      setLoading(false);
    }
  };

  useEffect(() => {
    let columnas;
    const esAdmin = user && user['cognito:groups'][0] === 'ADMIN';
    if (user && !(user['cognito:groups'][0] === 'ADMIN_ASEGURADORA')) {
      columnas = [
        {
          title: 'Fecha',
          dataIndex: 'fecha',
          width: '10%',
          filterType: fieldTypes.DATETIME,
          sortable: true
        },
        {
          title: 'Cliente',
          dataIndex: ['persona>nombres'],
          key: 'nombres',
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },
        {
          title: 'Celular',
          dataIndex: ['persona>celular'],
          key: 'celular',
          filterType: fieldTypes.STRING,
          sortable: true
        },
        {
          title: 'Correo',
          dataIndex: ['persona>correo'],
          key: 'correo',
          filterType: fieldTypes.STRING,
          sortable: true
        },
        {
          title: 'Producto',
          dataIndex: ['producto>descripcion'],
          key: 'cotizacionMasiva.producto.descripcion',
          width: '20%',
          filterType: fieldTypes.STRING,
          sortable: true
        },
        {
          title: 'Empresa',
          dataIndex: ['empresa'],
          key:
            'cotizacionMasiva->detalles->aseguradoraEmpresa->empresa"."nombre',
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },

        ...(esAdmin
          ? [
              {
                title: 'Aseguradora/s',
                dataIndex: ['aseguradora'],
                key: 'aseguradoraEmpresa.aseguradora.descripcion',
                filterType: fieldTypes.STRING,
                sortable: true,
                width: '200',
                render: (value) => {
                  // eslint-disable-next-line react/destructuring-assignment
                  const lista = (value && value.split(', ')) || [];
                  if (lista.length > 1) {
                    return (
                      <Popover
                        content={
                          <Space direction="vertical">
                            {lista.map((item) => (
                              <Typography.Text key={item}>
                                {item}
                              </Typography.Text>
                            ))}
                          </Space>
                        }
                        title="Aseguradoras"
                        trigger="hover"
                      >
                        <Button size="middle" type="link">
                          Ver todas
                        </Button>
                      </Popover>
                    );
                  }
                  return (
                    <Typography.Text
                      style={{
                        width: 200
                      }}
                    >
                      {value}
                    </Typography.Text>
                  );
                }
              }
            ]
          : []),
        {
          title: 'Ejecutivo',
          dataIndex: ['usuarioVendedor>nombres'],
          key: 'usuarioVendedor>nombres',
          filterType: fieldTypes.STRING,
          sortable: true,
          render: (nombres) => {
            return (
              nombres || (
                <Typography.Text type="secondary">
                  Sin Ejecutivo
                </Typography.Text>
              )
            );
          }
        },
        {
          title: 'Estado',
          dataIndex: 'estado',
          render: (estado) => {
            const estadoUpperCase = toUpperCase(estado);
            let color = 'white';
            if (estadoUpperCase === 'PENDIENTE') {
              color = 'yellow';
            } else if (estadoUpperCase === 'ENVIADO') {
              color = 'green';
            } else if (estadoUpperCase === 'RECHAZADO') {
              color = 'red';
            }
            return (
              <Tag color={color} key={estadoUpperCase}>
                {estadoUpperCase}
              </Tag>
            );
          },
          filterType: fieldTypes.ENUM,
          filters: Object.keys(ESTADOS_COTIZACION).map((key) => ({
            text: ESTADOS_COTIZACION[key],
            value: key
          })),
          sortable: true
        },
        {
          title: 'Sucursal',
          dataIndex: ['sucursal'],
          key: 'cotizacionMasiva.usuarioVendedor.sucursal.descripcion',
          filterType: fieldTypes.STRING,
          sortable: true
        },
        {
          title: 'Campaña',
          dataIndex: ['campaña'],
          key: 'campaña',
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },
        {
          title: 'Acciones',
          key: 'action',
          width: '10px',
          render: (text, record) => (
            <Space size="small">
              <Tooltip title="Continuar">
                <Button
                  type="text"
                  icon={<FormOutlined />}
                  size="middle"
                  disabled={record.estado.includes('RECHAZADO')}
                  onClick={() => {
                    if (record.id_producto === 1) {
                      handleOpenUrl(`vehiculo/${record.token}`);
                    } else if (record.id_producto === 5) {
                      handleOpenUrl(`comercio/${record.token}`);
                    } else {
                      handleOpenUrl(`solicitar/${record.token}/masiva`);
                    }
                  }}
                />
              </Tooltip>
              {
                // TODO AGREGAR TELEFONICA
              }
              <Tooltip title="Copiar enlace de Cotización">
                <Button
                  type="text"
                  disabled={record.estado.includes('RECHAZADO')}
                  icon={
                    <CopyOutlined style={{ color: CONFIG.COLOR_PRIMARY }} />
                  }
                  size="middle"
                  onClick={() => {
                    handleGetNewToken(record.token, record.id_producto);
                  }}
                />
              </Tooltip>
              <Tooltip
                title={
                  record.id_producto === 1 || record.id_producto === 5
                    ? 'Reenviar promoción'
                    : 'Reenviar cotización'
                }
              >
                <Button
                  type="text"
                  disabled={record.estado.includes('RECHAZADO')}
                  icon={<MessageOutlined />}
                  size="middle"
                  loading={loading && tokenEnviar === record.token}
                  onClick={() => enviarSmsMail(record)}
                />
              </Tooltip>
              {(user['cognito:groups'].includes('ADMIN') ||
                user['cognito:groups'].includes('ADMIN_EMPRESA') ||
                user['cognito:groups'].includes('ADMIN_SUCURSAL')) && (
                <Tooltip title="Asignar Ejecutivo">
                  <Button
                    disabled="true"
                    type="text"
                    icon={<UserAddOutlined />}
                    loading={loading && token === record.token}
                    size="middle"
                    onClick={() => showModal(record)}
                  />
                </Tooltip>
              )}
              {
                <Tooltip
                  title={
                    record.enviado === 'SI'
                      ? 'Visto por el cliente desde e-mail o SMS'
                      : 'Aún no ha visto el cliente'
                  }
                >
                  <Button
                    type="text"
                    style={{
                      ...(record.enviado === 'SI' && { color: green.primary }),
                      cursor: 'auto'
                    }}
                    icon={
                      record.enviado === 'SI' ? (
                        <CheckCircleOutlined />
                      ) : (
                        <MinusCircleOutlined />
                      )
                    }
                    size="middle"
                  />
                </Tooltip>
              }
            </Space>
          ),
          align: 'left'
        }
      ];
    } else {
      columnas = [
        {
          title: 'Fecha',
          dataIndex: 'fecha',
          width: '10%',
          filterType: fieldTypes.DATETIME,
          sortable: true
        },
        {
          title: 'Cliente',
          dataIndex: ['persona>nombres'],
          key: 'nombres',
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        }, // agregar el select para corredora en caso que aplique
        {
          title: 'Corredora',
          dataIndex: ['corredora'],
          key: '',
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },
        {
          title: 'Empresa',
          dataIndex: ['empresa'],
          key:
            'cotizacionMasiva->detalles->aseguradoraEmpresa->empresa"."nombre',
          filterType: fieldTypes.STRING,
          sortable: true,
          width: '20%'
        },
        {
          title: 'Producto',
          dataIndex: ['producto>descripcion'],
          key: 'cotizacionMasiva.producto.descripcion',
          width: '20%',
          filterType: fieldTypes.STRING,
          sortable: true
        },
        {
          title: 'Ejecutivo',
          dataIndex: ['usuarioVendedor>nombres'],
          key: 'usuarioVendedor>nombres',
          filterType: fieldTypes.STRING,
          sortable: true,
          render: (nombres) => {
            return (
              nombres || (
                <Typography.Text type="secondary">
                  Sin Ejecutivo
                </Typography.Text>
              )
            );
          }
        },
        {
          title: 'Acciones',
          key: 'action',
          width: '10px',
          render: (text, record) => (
            <Space size="small">
              <Tooltip title="Ver solicitud">
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  size="middle"
                  onClick={() => handleOpenUrl(`solicitar/${record.token}`)}
                />
              </Tooltip>
            </Space>
          ),
          align: 'left'
        }
      ];
    }
    setColumns(columnas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, tokenEnviar, token]);

  const mapOptions = ({ id: value, nombres: label }) => ({
    value,
    label
  });
  const fetchListUsuario = (searchText) => {
    return instanceRef.instance.get(`auth/users`, {
      params: {
        current: 1,
        pageSize: 10,
        filters: {
          nombres: searchText
        }
      }
    });
  };

  const showModal = (record) => {
    setVisible(true);
    setToken(record.token);
  };

  const handleOk = async () => {
    const ejecutivoSelected = formUser.getFieldValue('usuario');
    if (token) {
      setVisible(false);
      try {
        const dataEjecutivo = {
          token,
          ejecutivoSelected
        };
        setLoading(true);
        await instanceRef.instance.post(
          `cotizacion/asignarEjecutivo`,
          dataEjecutivo
        );
        message.success('Ejecutivo actualizado correctamente');
        tableRef.current.reload();
      } catch (error) {
        errorHandler(error, 'No se pudo asignar el ejecutivo');
      }
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const getRender = () => {
    if (user) {
      return (
        <ResponsiveContainer size="large" style={{ paddingTop: 0 }}>
          <TableAndFilters
            withGeneralFilter
            ref={tableRef}
            url="/cotizacionmasiva"
            title="Cotizaciones Masivas"
            columns={columns}
            showTable
            rowKey={(record) => record.token}
          />

          <Modal
            title="Asignar Ejecutivo"
            open={visible}
            onOk={handleOk}
            onCancel={handleCancel}
          >
            <Form
              form={formUser}
              layout="vertical"
              name="usuarioForm"
              style={styles.form}
            >
              <Form.Item
                label="Ejecutivo"
                name="usuario"
                rules={[{ required: true }]}
              >
                <CustomAutocomplete
                  placeholder="Buscar"
                  fetchOptions={fetchListUsuario}
                  mapOptions={mapOptions}
                />
              </Form.Item>
            </Form>
          </Modal>
        </ResponsiveContainer>
      );
    }
    return <p> </p>;
  };

  return getRender();
}

export default CotizacionMasivaList;
